import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as RC from 'recharts';
import { isSOPDraft } from '../lib/sopDraft';
import {
  Users, UserPlus, Plus, Trash2, Shield, UserCog, Copy, CheckCircle2, XCircle,
  Link2, RefreshCw, KeyRound, Settings2, ShieldAlert, Activity, ClipboardList, Send,
  BarChart3, Scale, Mail, CalendarDays, Clock, Play, FileText, TrendingUp, AlertTriangle,
  Sparkles, ArrowRight, BookOpen, FileSignature, ClipboardCheck, Pencil, Eye, FilePlus2,
  Copy as CopyIcon, Upload, X, Lock,
} from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { SECTIONS } from '../lib/sectionTheme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const M = SECTIONS.management;

function Modal({ title, children, onClose, size = 'sm' }) {
  const widths = { sm: 'max-w-lg', md: 'max-w-2xl', lg: 'max-w-4xl' };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className={`bg-card border border-border rounded-2xl ${widths[size]} w-full p-5 max-h-[90vh] overflow-y-auto`}>
        <h3 className="font-['Outfit'] text-lg font-bold text-foreground mb-3">{title}</h3>
        {children}
      </div>
    </div>
  );
}

export default function ManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const canManage = isAdmin || user?.role === 'superuser';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  // Forms
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'reception', sop_assignments: [] });
  const [createForm, setCreateForm] = useState({ email: '', name: '', password: '', role: 'reception', sop_assignments: [] });
  const [sopsList, setSopsList] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({ bundles: {}, default_due_days: 14 });
  const [dash, setDash] = useState(null);
  const [compliance, setCompliance] = useState(null);

  useEffect(() => {
    if (!canManage) { navigate('/'); return; }
    loadUsers();
    loadSops();
    loadRoleDefaults();
    axios.get(`${API}/dashboard/admin`, { withCredentials: true }).then(r => setDash(r.data)).catch(() => {});
    axios.get(`${API}/sops/compliance`, { withCredentials: true }).then(r => setCompliance(r.data)).catch(() => {});
  }, [canManage, navigate]);

  const loadSops = async () => {
    try {
      const r = await axios.get(`${API}/sops`, { withCredentials: true });
      setSopsList(r.data);
    } catch { /* */ }
  };

  const loadRoleDefaults = async () => {
    try {
      const r = await axios.get(`${API}/role-sop-defaults`, { withCredentials: true });
      setRoleDefaults(r.data);
    } catch { /* */ }
  };

  // Convert a role's default SOP bundle into picker rows [{sop_id, due_date}].
  // Due date = today + default_due_days.
  const bundleFor = (role) => {
    const ids = roleDefaults.bundles?.[role] || [];
    const due = new Date();
    due.setDate(due.getDate() + (roleDefaults.default_due_days || 14));
    const dueStr = due.toISOString().slice(0, 10);
    return ids.map(sop_id => ({ sop_id, due_date: dueStr }));
  };

  const setInviteRole = (newRole) => {
    setInviteForm(f => ({ ...f, role: newRole, sop_assignments: bundleFor(newRole) }));
  };
  const setCreateRole = (newRole) => {
    setCreateForm(f => ({ ...f, role: newRole, sop_assignments: bundleFor(newRole) }));
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(r.data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/users/invite`, inviteForm, { withCredentials: true });
      setLastInvite(r.data);
      setInviteForm({ email: '', name: '', role: 'reception', sop_assignments: [] });
      loadUsers();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to create invite');
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users/create`, createForm, { withCredentials: true });
      setCreateOpen(false);
      setCreateForm({ email: '', name: '', password: '', role: 'reception', sop_assignments: [] });
      loadUsers();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to create user');
    }
  };

  const copyInviteLink = () => {
    if (!lastInvite?.invite_link) return;
    navigator.clipboard.writeText(lastInvite.invite_link);
  };

  const promote = async (uid) => {
    try {
      await axios.put(`${API}/users/${uid}/role?role=superuser`, {}, { withCredentials: true });
      loadUsers();
    } catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
  };
  const demote = async (uid) => {
    try {
      await axios.put(`${API}/users/${uid}/role?role=reception`, {}, { withCredentials: true });
      loadUsers();
    } catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
  };
  const deactivate = async (uid) => {
    if (!window.confirm('Deactivate this user? They will not be able to log in.')) return;
    try {
      await axios.delete(`${API}/users/${uid}`, { withCredentials: true });
      loadUsers();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to deactivate');
    }
  };
  const activate = async (uid) => {
    try {
      await axios.put(`${API}/users/${uid}/activate`, {}, { withCredentials: true });
      loadUsers();
    } catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
  };
  const reset2fa = async (uid, name) => {
    if (!window.confirm(`Reset 2FA for ${name}?\n\nThey will see a fresh QR code on next login. Use this if they've lost their authenticator device.`)) return;
    try {
      await axios.post(`${API}/users/${uid}/reset-2fa`, {}, { withCredentials: true });
      loadUsers();
    } catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="management-page">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Greeting — visible only on the admin management hub */}
        <div className="mb-8" data-testid="mgmt-greeting">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4" style={{ color: M.color }} />
            <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: M.color }}>Management dashboard</span>
          </div>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Hello, <span style={{ color: M.color }}>{user.name?.split(' ')[0]}</span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
            Track team training, monitor MBS fees, and keep SOPs compliant — all in one place.
          </p>
        </div>

        {/* Management Overview: KPIs + alert tiles */}
        {dash && <ManagementOverview dash={dash} compliance={compliance} />}

        {/* Explore sections — quick jump to every module */}
        <div className="mt-2 mb-4">
          <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Explore sections</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Each section has its own AI assistant trained on its knowledge base</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-testid="section-entry-cards">
          <SectionCard to="/k4" section={SECTIONS.training} icon={BookOpen}
            title="Kestral K4 Resource" desc="Reference library & AI assistant" />
          <SectionCard to="/mbs" section={SECTIONS.mbs} icon={Activity}
            title="MBS Monitor" desc="Medicare fee schedule changes & alerts"
            stat={dash?.mbs_changes ?? '—'} statLabel="Fee changes" />
          <SectionCard to="/sops" section={SECTIONS.sops} icon={ClipboardCheck}
            title="SOPs" desc="Standard Operating Procedures"
            stat={dash?.sop_pending ?? '—'} statLabel="Pending tasks" />
        </div>

        {/* Quick tools grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-10" data-testid="mgmt-tools">
          <Tool to="/dashboard" icon={BarChart3} label="Practice analytics" sub="KPIs, alerts, digest" />
          <Tool to="/dashboard/logins" icon={ShieldAlert} label="Login activity" sub="Audit trail & alerts" color="#DC2626" />
          <Tool to="/sops/governance" icon={Shield} label="Governance dashboard" sub="SOP review sign-off" color="#7C3AED" />
          <Tool to="/disputes" icon={Scale} label="Conflict register" sub="Staff disputes & mediation" color="#7C3AED" />
        </div>

        {/* Custom SOP library — admins can create/edit/delete their own SOPs */}
        <CustomSOPManager />

        {/* Custom Prep Guide library — admins can author patient prep sheets */}
        <CustomPrepManager />

        {/* Screening submissions dashboard — patient-filled-in screening forms */}
        <ScreeningSubmissionsWidget />

        {/* Incident dashboard — every reported incident, with triage / RCA / close */}
        <IncidentsDashboardWidget />

        {/* Consent submissions — signed image-guided procedure consents */}
        <ConsentSubmissionsWidget />

        {/* SOP Assignment widget */}
        <AssignSOPWidget />

        {/* Scheduler status */}
        <CronStatusWidget />

        {/* Users section */}
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="mgmt-users-section">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" style={{ color: M.color }} />
                <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">User accounts</h2>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{users.length} total · {users.filter(u => u.active !== false).length} active</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => {
                setInviteOpen(true); setLastInvite(null);
                setInviteForm(f => ({ ...f, sop_assignments: bundleFor(f.role) }));
              }} data-testid="mgmt-invite-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border"
                style={{ borderColor: `${M.color}40`, color: M.color }}>
                <Link2 className="w-4 h-4" /> Create invite link
              </button>
              <button onClick={() => {
                setCreateOpen(true);
                setCreateForm(f => ({ ...f, sop_assignments: bundleFor(f.role) }));
              }} data-testid="mgmt-create-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
                style={{ backgroundColor: M.color }}>
                <UserPlus className="w-4 h-4" /> Create account
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
            ) : users.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground">No users yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Role</th>
                    <th className="px-3 py-2">2FA</th>
                    <th className="px-3 py-2">Charter</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <tr key={u.id} className="border-b border-border/60 last:border-0" data-testid={`mgmt-user-row-${i}`}>
                      <td className="px-3 py-2 text-foreground font-medium">{u.name || '—'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{u.email}</td>
                      <td className="px-3 py-2">
                        <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: u.role === 'admin' ? '#DC262615' : u.role === 'superuser' ? `${M.color}15` : '#64748B15',
                                   color: u.role === 'admin' ? '#DC2626' : u.role === 'superuser' ? M.color : '#64748B' }}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {u.totp_enabled ? <CheckCircle2 className="w-4 h-4" style={{ color: '#16A34A' }} /> : <XCircle className="w-4 h-4 text-muted-foreground" />}
                      </td>
                      <td className="px-3 py-2">
                        {u.culture_charter_accepted_at ? <CheckCircle2 className="w-4 h-4" style={{ color: '#16A34A' }} /> : <XCircle className="w-4 h-4 text-muted-foreground" />}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[11px] font-bold uppercase tracking-wider ${u.active !== false ? 'text-green-500' : 'text-red-500'}`}>
                          {u.active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-0.5 justify-end">
                          {u.role !== 'admin' && u.role !== 'superuser' && <button onClick={() => promote(u.id)} title="Promote to superuser" data-testid={`mgmt-promote-${i}`} className="p-1.5 text-muted-foreground hover:text-foreground"><UserCog className="w-3.5 h-3.5" /></button>}
                          {u.role === 'superuser' && <button onClick={() => demote(u.id)} title="Demote to reception" data-testid={`mgmt-demote-${i}`} className="p-1.5 text-muted-foreground hover:text-foreground"><UserCog className="w-3.5 h-3.5" /></button>}
                          {u.totp_enabled && <button onClick={() => reset2fa(u.id, u.name || u.email)} title="Reset 2FA" data-testid={`mgmt-reset-2fa-${i}`} className="p-1.5 text-muted-foreground hover:text-amber-500"><KeyRound className="w-3.5 h-3.5" /></button>}
                          {u.active !== false ? (
                            <button onClick={() => deactivate(u.id)} title="Deactivate" data-testid={`mgmt-deactivate-${i}`} className="p-1.5 text-muted-foreground hover:text-red-500"><XCircle className="w-3.5 h-3.5" /></button>
                          ) : (
                            <button onClick={() => activate(u.id)} title="Activate" data-testid={`mgmt-activate-${i}`} className="p-1.5 text-muted-foreground hover:text-green-500"><CheckCircle2 className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Invite modal */}
      {inviteOpen && (
        <Modal title="Create invite link" onClose={() => { setInviteOpen(false); setLastInvite(null); }}>
          {lastInvite ? (
            <div className="space-y-3" data-testid="invite-created">
              <p className="text-sm text-foreground">Invite link for <strong>{lastInvite.email}</strong>. Expires in 7 days.</p>
              {lastInvite.email_status === 'failed' && (
                <div className="flex items-start gap-2 p-2 rounded-lg border border-amber-500/30 bg-amber-500/5 text-xs text-amber-600" data-testid="invite-email-failed">
                  <span>⚠</span>
                  <span>Email delivery failed (Resend sandbox / unverified domain). <strong>Copy the link below and send it manually.</strong></span>
                </div>
              )}
              <div className="flex items-center gap-2 bg-muted border border-border rounded-lg p-2">
                <input readOnly value={lastInvite.invite_link} data-testid="invite-link-input" className="flex-1 bg-transparent text-xs text-foreground font-mono outline-none" />
                <button onClick={copyInviteLink} data-testid="invite-copy-btn" className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded border border-border"><Copy className="w-3 h-3" /> Copy</button>
              </div>
              <p className="text-[11px] text-muted-foreground">Email this link to {lastInvite.email}. They&apos;ll click it to set a password and get access. (Resend email will also fire if the domain is verified.)</p>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setLastInvite(null)} className="text-sm px-3 py-1.5 rounded-lg border border-border">Create another</button>
                <button onClick={() => { setInviteOpen(false); setLastInvite(null); }} className="text-sm px-3 py-1.5 rounded-lg text-white" style={{ backgroundColor: M.color }}>Done</button>
              </div>
            </div>
          ) : (
            <form onSubmit={submitInvite} className="space-y-3">
              <Input label="Full name" value={inviteForm.name} onChange={v => setInviteForm({ ...inviteForm, name: v })} required testid="invite-name" />
              <Input label="Email" type="email" value={inviteForm.email} onChange={v => setInviteForm({ ...inviteForm, email: v })} required testid="invite-email" />
              <RoleSelect value={inviteForm.role} onChange={setInviteRole} testid="invite-role" />
              <SopAssignPicker
                value={inviteForm.sop_assignments}
                onChange={list => setInviteForm({ ...inviteForm, sop_assignments: list })}
                sops={sopsList}
                testid="invite-sop-picker" />
              <p className="text-[11px] text-muted-foreground">Listed SOPs become mandatory tasks the moment they sign up. They can still browse the rest of the SOP library as reference.</p>
              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button type="button" onClick={() => setInviteOpen(false)} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
                <button type="submit" data-testid="invite-submit" className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white" style={{ backgroundColor: M.color }}>
                  <Send className="w-3.5 h-3.5" /> Generate link
                </button>
              </div>
            </form>
          )}
        </Modal>
      )}

      {/* Create account modal */}
      {createOpen && (
        <Modal title="Create account directly" onClose={() => setCreateOpen(false)}>
          <form onSubmit={submitCreate} className="space-y-3">
            <p className="text-xs text-muted-foreground">Creates an account with a password you choose. The user will still set up TOTP and accept the Culture Charter on their first login.</p>
            <Input label="Full name" value={createForm.name} onChange={v => setCreateForm({ ...createForm, name: v })} required testid="create-name" />
            <Input label="Email" type="email" value={createForm.email} onChange={v => setCreateForm({ ...createForm, email: v })} required testid="create-email" />
            <Input label="Initial password (min 8)" type="password" value={createForm.password} onChange={v => setCreateForm({ ...createForm, password: v })} required testid="create-password" minLength={8} />
            <RoleSelect value={createForm.role} onChange={setCreateRole} testid="create-role" />
            <SopAssignPicker
              value={createForm.sop_assignments}
              onChange={list => setCreateForm({ ...createForm, sop_assignments: list })}
              sops={sopsList}
              testid="create-sop-picker" />
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={() => setCreateOpen(false)} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
              <button type="submit" data-testid="create-submit" className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white" style={{ backgroundColor: M.color }}>
                <UserPlus className="w-3.5 h-3.5" /> Create
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Tool({ to, icon: Icon, label, sub, color }) {
  const c = color || M.color;
  return (
    <Link to={to} data-testid={`mgmt-tool-${to.replace(/\W+/g, '-')}`}
      className="flex items-start gap-3 p-4 bg-card border border-border rounded-2xl hover:shadow-sm transition-all">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${c}15`, color: c }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-['Outfit'] text-sm font-semibold text-foreground">{label}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>
      </div>
    </Link>
  );
}

function Input({ label, type = 'text', value, onChange, required, testid, minLength }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required} minLength={minLength} data-testid={testid}
        className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
    </label>
  );
}

function RoleSelect({ value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Role</span>
      <select value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
        <option value="superuser">Super user (PM) — admin-level: user mgmt, compliance, everything</option>
        <option value="radiologist">Radiologist</option>
        <option value="reception">Reception</option>
        <option value="radiographer">Radiographer / Technologist</option>
        <option value="sonographer">Sonographer</option>
        <option value="nursing">Nursing</option>
        <option value="trainee">Trainee</option>
      </select>
    </label>
  );
}

function SopAssignPicker({ value, onChange, sops, testid = 'sop-picker' }) {
  const rows = value || [];
  // Map sop_id -> row for quick lookup
  const byId = Object.fromEntries(rows.map(r => [r.sop_id, r]));
  // Default due date = today + 14 days
  const defaultDue = (() => {
    const d = new Date(); d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  })();
  const realSops = sops.filter(s =>
    (s.category || '').toLowerCase() !== 'test' &&
    (s.category || '').toLowerCase() !== 'testing' &&
    !isSOPDraft(s)
  );
  const hiddenDraftCount = sops.filter(s =>
    (s.category || '').toLowerCase() !== 'test' &&
    (s.category || '').toLowerCase() !== 'testing' &&
    isSOPDraft(s)
  ).length;

  const toggle = (sop_id) => {
    if (byId[sop_id]) {
      onChange(rows.filter(r => r.sop_id !== sop_id));
    } else {
      onChange([...rows, { sop_id, due_date: defaultDue }]);
    }
  };
  const setDate = (sop_id, due_date) => {
    onChange(rows.map(r => r.sop_id === sop_id ? { ...r, due_date } : r));
  };
  const checkAll = () => onChange(realSops.map(s => ({ sop_id: s.sop_id, due_date: byId[s.sop_id]?.due_date || defaultDue })));
  const clearAll = () => onChange([]);

  return (
    <div className="border border-border rounded-lg" data-testid={testid}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Auto-assign SOPs · {rows.length} / {realSops.length} ticked
        </span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={checkAll} data-testid={`${testid}-all`}
            className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border hover:bg-accent">Tick all</button>
          <button type="button" onClick={clearAll} data-testid={`${testid}-none`}
            className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border hover:bg-accent">Clear</button>
        </div>
      </div>
      <ul className="max-h-72 overflow-y-auto">
        {realSops.map(s => {
          const row = byId[s.sop_id];
          const checked = !!row;
          return (
            <li key={s.sop_id}
              className="grid grid-cols-[auto_1fr_130px] items-center gap-3 px-3 py-2 border-b border-border/40 last:border-0 hover:bg-accent/30"
              data-testid={`${testid}-row-${s.sop_id}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(s.sop_id)}
                data-testid={`${testid}-check-${s.sop_id}`}
                className="w-4 h-4 rounded border-border" />
              <div className="min-w-0">
                <span className="text-sm text-foreground font-medium block truncate">{s.title}</span>
                <span className="text-[11px] text-muted-foreground">{s.category}{s.version ? ` · v${s.version}` : ''}</span>
              </div>
              <input type="date" value={row?.due_date || ''} onChange={e => setDate(s.sop_id, e.target.value)}
                disabled={!checked}
                data-testid={`${testid}-due-${s.sop_id}`}
                className="text-xs bg-background border border-border rounded-md px-2 py-1 disabled:opacity-40" />
            </li>
          );
        })}
      </ul>
      {hiddenDraftCount > 0 && (
        <div className="px-3 py-2 border-t border-border bg-amber-500/5 text-[10px] text-muted-foreground"
          data-testid={`${testid}-drafts-hidden`}>
          <span className="font-semibold text-amber-700 dark:text-amber-400">{hiddenDraftCount} draft SOP{hiddenDraftCount === 1 ? '' : 's'}</span>{' '}
          hidden from assignment — finalise on <Link to="/sops" className="underline hover:text-foreground">the SOPs page</Link> to enable.
        </div>
      )}
    </div>
  );
}

function AssignSOPWidget() {
  const [users, setUsers] = useState([]);
  const [sops, setSops] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({ bundles: {}, default_due_days: 14 });
  const [selectedUser, setSelectedUser] = useState('');
  // picks: sop_id -> { checked: bool, due_date: 'YYYY-MM-DD' }
  const [picks, setPicks] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    try {
      const [u, s, a, rd] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/sops`, { withCredentials: true }),
        axios.get(`${API}/sops/assignments`, { withCredentials: true }),
        axios.get(`${API}/role-sop-defaults`, { withCredentials: true }),
      ]);
      setUsers(u.data);
      setSops(s.data);
      setAssignments(a.data);
      setRoleDefaults(rd.data);
    } catch { /* */ }
  };
  useEffect(() => { load(); }, []);

  const userMap = Object.fromEntries(users.map(u => [u.id, u]));
  const sopMap = Object.fromEntries(sops.map(s => [s.sop_id, s]));
  const activeUsers = users.filter(u => u.active !== false);
  const selectedUserDoc = userMap[selectedUser];
  const userAssignments = assignments.filter(a => a.user_id === selectedUser);
  const userAssignmentBySop = Object.fromEntries(userAssignments.map(a => [a.sop_id, a]));
  // Hide test SOPs from the picker so the list stays clean.
  const realSops = sops.filter(s =>
    (s.category || '').toLowerCase() !== 'test' &&
    (s.category || '').toLowerCase() !== 'testing' &&
    !isSOPDraft(s)
  );

  // When a user is selected (or data reloads), seed picks from their current assignments.
  useEffect(() => {
    if (!selectedUser) { setPicks({}); return; }
    const today = new Date();
    today.setDate(today.getDate() + (roleDefaults.default_due_days || 14));
    const defaultDue = today.toISOString().slice(0, 10);
    const next = {};
    for (const s of realSops) {
      const existing = userAssignmentBySop[s.sop_id];
      next[s.sop_id] = {
        checked: !!existing,
        due_date: existing?.due_date || defaultDue,
      };
    }
    setPicks(next);
    setMsg('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, assignments.length, roleDefaults.default_due_days]);

  const togglePick = (sop_id) => setPicks(p => ({ ...p, [sop_id]: { ...p[sop_id], checked: !p[sop_id]?.checked } }));
  const setPickDate = (sop_id, date) => setPicks(p => ({ ...p, [sop_id]: { ...p[sop_id], due_date: date } }));

  const applyRoleDefaults = () => {
    if (!selectedUserDoc) return;
    const bundle = roleDefaults.bundles?.[selectedUserDoc.role] || [];
    setPicks(p => {
      const next = { ...p };
      // Tick every SOP in the bundle, leave everything else as-is (incl. completed).
      for (const sop_id of bundle) {
        next[sop_id] = { ...next[sop_id], checked: true };
      }
      return next;
    });
  };

  const save = async () => {
    if (!selectedUser) return;
    setSaving(true); setMsg('');
    let created = 0, removed = 0, skipped = 0;
    try {
      for (const sop_id of Object.keys(picks)) {
        const pick = picks[sop_id];
        const existing = userAssignmentBySop[sop_id];
        if (pick.checked && !existing) {
          await axios.post(`${API}/sops/assign`,
            { user_id: selectedUser, sop_id, due_date: pick.due_date || new Date().toISOString().slice(0, 10) },
            { withCredentials: true });
          created++;
        } else if (!pick.checked && existing) {
          if (existing.status === 'completed') { skipped++; continue; }
          await axios.delete(`${API}/sops/assignments/${existing.assignment_id}`, { withCredentials: true });
          removed++;
        }
      }
      await load();
      setMsg(`✓ ${created} added, ${removed} removed${skipped ? `, ${skipped} completed-skipped` : ''}`);
      setTimeout(() => setMsg(''), 4000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const countsForUser = (uid) => {
    const rows = assignments.filter(a => a.user_id === uid);
    const done = rows.filter(r => r.status === 'completed').length;
    return { total: rows.length, done };
  };

  const dirty = selectedUser && Object.keys(picks).some(sop_id => {
    const existing = userAssignmentBySop[sop_id];
    const p = picks[sop_id];
    return (p.checked && !existing) || (!p.checked && existing && existing.status !== 'completed');
  });

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-10" data-testid="mgmt-assign-sops">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList className="w-4 h-4" style={{ color: M.color }} />
        <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Staff SOP assignments</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-5">Pick a staff member, then tick the SOPs they must read. Click <span className="font-semibold">"Apply role defaults"</span> to one-tap-tick the recommended bundle for their role, then adjust as needed.</p>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        {/* Staff list */}
        <nav className="border border-border rounded-xl p-2 max-h-[560px] overflow-y-auto" data-testid="staff-list">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-2 py-1.5">Staff · {activeUsers.length}</p>
          <ul className="space-y-1">
            {activeUsers.map(u => {
              const isActive = u.id === selectedUser;
              const c = countsForUser(u.id);
              return (
                <li key={u.id}>
                  <button onClick={() => setSelectedUser(u.id)} data-testid={`staff-pick-${u.id}`}
                    className="w-full text-left px-2.5 py-2 rounded-lg transition-colors flex items-center justify-between gap-2"
                    style={isActive ? { backgroundColor: `${M.color}15`, color: M.color } : {}}>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold truncate" style={{ color: isActive ? M.color : undefined }}>{u.name}</span>
                      <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">{u.role}</span>
                    </span>
                    {c.total > 0 && (
                      <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: c.done === c.total ? '#16A34A15' : '#D9770615', color: c.done === c.total ? '#16A34A' : '#D97706' }}>
                        {c.done}/{c.total}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* SOP checkbox matrix */}
        <div className="border border-border rounded-xl p-4">
          {!selectedUser ? (
            <div className="text-center py-16 text-sm text-muted-foreground">Select a staff member on the left to view and edit their SOP assignments.</div>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">{selectedUserDoc?.name}</h3>
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{selectedUserDoc?.role}</p>
                </div>
                <button type="button" onClick={applyRoleDefaults} data-testid="apply-role-defaults"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border"
                  style={{ borderColor: `${M.color}40`, color: M.color }}>
                  <RefreshCw className="w-3 h-3" /> Apply role defaults
                </button>
              </div>

              <ul className="space-y-1" data-testid="sop-matrix">
                {realSops.map(s => {
                  const p = picks[s.sop_id] || { checked: false, due_date: '' };
                  const existing = userAssignmentBySop[s.sop_id];
                  const isDefault = (roleDefaults.bundles?.[selectedUserDoc?.role] || []).includes(s.sop_id);
                  const isCompleted = existing?.status === 'completed';
                  return (
                    <li key={s.sop_id}
                      className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-2 py-2 rounded-lg hover:bg-accent/50 transition-colors"
                      data-testid={`sop-matrix-row-${s.sop_id}`}>
                      <input type="checkbox" checked={p.checked} onChange={() => togglePick(s.sop_id)}
                        disabled={isCompleted}
                        data-testid={`sop-check-${s.sop_id}`}
                        className="w-4 h-4 rounded border-border accent-[--i3-teal]" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-foreground font-medium">{s.title}</span>
                          {isDefault && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${M.color}15`, color: M.color }}>default</span>}
                          {isCompleted && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-green-500/15 text-green-600">completed</span>}
                          {existing && !isCompleted && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600">pending</span>}
                        </div>
                        <p className="text-[11px] text-muted-foreground">{s.category} {s.version ? `· v${s.version}` : ''}</p>
                      </div>
                      <input type="date" value={p.due_date} onChange={e => setPickDate(s.sop_id, e.target.value)}
                        disabled={!p.checked || isCompleted}
                        data-testid={`sop-due-${s.sop_id}`}
                        className="text-xs bg-background border border-border rounded-md px-2 py-1 disabled:opacity-40" />
                    </li>
                  );
                })}
              </ul>

              <div className="mt-5 pt-4 border-t border-border flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs" style={{ color: msg.startsWith('✓') ? '#16A34A' : msg ? '#DC2626' : 'hsl(var(--muted-foreground))' }}>
                  {msg || 'Changes are queued. Click Save to apply.'}
                </p>
                <button onClick={save} disabled={!dirty || saving} data-testid="save-sop-matrix"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-40"
                  style={{ backgroundColor: M.color }}>
                  <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function CronStatusWidget() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');

  const load = async () => {
    try {
      const r = await axios.get(`${API}/cron/status`, { withCredentials: true });
      setData(r.data);
    } catch { /* */ }
  };
  useEffect(() => { load(); }, []);

  const runNow = async (id) => {
    if (!window.confirm(`Run '${id}' now? This sends real emails / hits real endpoints.`)) return;
    setBusy(id);
    try {
      await axios.post(`${API}/cron/run-now/${id}`, {}, { withCredentials: true });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
    setBusy('');
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : '—';
  if (!data) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-10" data-testid="mgmt-cron-status">
      <div className="flex items-center gap-2 mb-1">
        <Clock className="w-4 h-4" style={{ color: M.color }} />
        <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Scheduled jobs</h2>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ml-2 ${data.running ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}
              data-testid="cron-running-state">
          {data.running ? 'Running' : 'Stopped'}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Background cron runs automatically. Use "Run now" to test or catch up manually.</p>

      <div className="grid sm:grid-cols-2 gap-3">
        {(data.jobs || []).map((j) => {
          const last = (data.last_runs || {})[j.id];
          return (
            <div key={j.id} className="border border-border rounded-xl p-4" data-testid={`cron-job-${j.id}`}>
              <p className="text-sm font-semibold text-foreground">{j.name}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Next run: <span className="text-foreground">{fmt(j.next_run)}</span></p>
              <p className="text-[11px] text-muted-foreground">
                Last run: {last ? (
                  <span className={last.status === 'success' ? 'text-green-600' : 'text-red-600'}>{last.status} · {fmt(last.ran_at)}</span>
                ) : <span className="text-muted-foreground">never</span>}
              </p>
              <button onClick={() => runNow(j.id)} disabled={busy === j.id} data-testid={`cron-run-${j.id}`}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-border hover:bg-accent disabled:opacity-40">
                <Play className="w-3 h-3" /> {busy === j.id ? 'Running…' : 'Run now'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function ManagementOverview({ dash, compliance }) {
  const rate = compliance?.rate ?? 0;
  const rateColor = rate >= 80 ? '#16A34A' : rate >= 50 ? '#F59E0B' : '#EF4444';
  return (
    <div className="space-y-4 mb-10" data-testid="mgmt-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Users} label="Active staff" value={dash.total_staff} color={M.color} />
        <KPI icon={CheckCircle2} label="Training completions" value={dash.training_completions} color="#16A34A" />
        <KPI icon={FileText} label="Active SOPs" value={dash.sop_count} color="#7C3AED" />
        <KPI icon={TrendingUp} label="SOP compliance" value={`${rate}%`} color={rateColor} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <AlertTile icon={AlertTriangle} label="Overdue SOPs" value={dash.sop_overdue} severity="danger" />
        <AlertTile icon={Clock} label="Pending SOPs" value={dash.sop_pending} severity="warn" />
        <AlertTile icon={Activity} label="MBS fee changes" value={dash.mbs_changes} severity="info" />
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <p className="font-['Outfit'] text-3xl font-bold text-foreground">{value}</p>
    </div>
  );
}

function AlertTile({ icon: Icon, label, value, severity }) {
  const colors = {
    danger: { border: value > 0 ? 'rgba(239,68,68,0.4)' : 'hsl(var(--border))', bg: value > 0 ? 'rgba(239,68,68,0.05)' : 'hsl(var(--card))', text: value > 0 ? '#EF4444' : 'hsl(var(--foreground))' },
    warn:   { border: value > 0 ? 'rgba(245,158,11,0.4)' : 'hsl(var(--border))', bg: value > 0 ? 'rgba(245,158,11,0.05)' : 'hsl(var(--card))', text: value > 0 ? '#F59E0B' : 'hsl(var(--foreground))' },
    info:   { border: value > 0 ? 'rgba(37,99,235,0.4)'  : 'hsl(var(--border))', bg: value > 0 ? 'rgba(37,99,235,0.05)'  : 'hsl(var(--card))', text: value > 0 ? '#2563EB' : 'hsl(var(--foreground))' },
  };
  const c = colors[severity];
  return (
    <div className="border rounded-2xl p-5" style={{ borderColor: c.border, backgroundColor: c.bg }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" style={{ color: c.text }} />
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <p className="font-['Outfit'] text-3xl font-bold" style={{ color: c.text }}>{value}</p>
    </div>
  );
}

function SectionCard({ to, section, icon: Icon, title, desc, stat, statLabel }) {
  return (
    <Link to={to} data-testid={`section-card-${section.id}`}
      className="group relative overflow-hidden bg-card border border-border rounded-2xl p-6 hover:shadow-lg transition-all"
      style={{ borderLeftWidth: 4, borderLeftColor: section.color }}>
      <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full opacity-5 group-hover:opacity-10 transition-opacity" style={{ backgroundColor: section.color }} />
      <div className="flex items-start justify-between mb-4 relative">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: section.colorBg }}>
          <Icon className="w-5 h-5" style={{ color: section.color }} strokeWidth={1.7} />
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
      </div>
      <h3 className="font-['Outfit'] text-base font-semibold text-foreground relative">{title}</h3>
      <p className="text-xs text-muted-foreground mt-1 relative">{desc}</p>
      {(stat !== undefined && stat !== null) && (
        <div className="flex items-baseline gap-2 mt-4 pt-4 border-t border-border relative">
          <span className="font-['Outfit'] text-2xl font-bold" style={{ color: section.color }}>{stat}</span>
          <span className="text-[11px] text-muted-foreground uppercase tracking-wider">{statLabel}</span>
        </div>
      )}
    </Link>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Custom SOP Manager
// Admin / Superuser only. Lists every SOP flagged `is_custom: true` and exposes
// create / edit / delete. Seeded governance SOPs are intentionally excluded so
// the UI can never mutate first-party content.
// ─────────────────────────────────────────────────────────────────────────────

const SOP_CATEGORY_PRESETS = [
  'Clinical Governance',
  'Clinical Safety — Infection Control',
  'Clinical Safety — Contrast Media',
  'Clinical Safety — Emergency Response',
  'Clinical Safety — Incident Management',
  'Patient Safety',
  'Patient Experience & Staff Safety',
  'HR & Contracts',
  'Work Health & Safety',
  'Operations',
];

function parseMarkdownSteps(md) {
  if (!md) return [];
  const lines = md.split('\n');
  const steps = [];
  let current = { title: 'Overview', body: [] };
  for (const ln of lines) {
    const m = ln.match(/^##\s+(.+?)\s*$/);
    if (m) {
      if (current.body.length || steps.length) {
        steps.push({ title: current.title, body: current.body.join('\n').trim() });
      }
      current = { title: m[1].trim(), body: [] };
    } else {
      current.body.push(ln);
    }
  }
  if (current.body.length || steps.length === 0) {
    steps.push({ title: current.title, body: current.body.join('\n').trim() });
  }
  return steps.filter(s => s.title || s.body);
}

function CustomSOPManager() {
  const [sops, setSops] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | <sop object>
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sops`, { withCredentials: true });
      setSops(r.data.filter(s => s.is_custom));
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load custom SOPs');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onSaved = () => { setEditing(null); load(); };

  const onDelete = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?\n\nThis also removes any existing staff assignments for this SOP.`)) return;
    try {
      await axios.delete(`${API}/sops/${s.sop_id}`, { withCredentials: true });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const onDuplicate = async (s) => {
    try {
      await axios.post(`${API}/sops`, {
        title: `${s.title} (copy)`,
        category: s.category,
        version: '1.0',
        iso_reference: s.iso_reference || '',
        content: s.content,
        video_url: s.video_url || '',
      }, { withCredentials: true });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Duplicate failed');
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-10" data-testid="mgmt-custom-sops">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4" style={{ color: M.color }} />
            <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Custom SOP library</h2>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Admin-authored SOPs. Use <code className="font-mono bg-muted px-1 rounded">##&nbsp;Step&nbsp;title</code> in markdown to auto-split into the step-by-step walkthrough.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setImporting(true)} data-testid="custom-sop-import-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border"
            style={{ borderColor: `${M.color}40`, color: M.color }}>
            <Upload className="w-4 h-4" /> Import markdown
          </button>
          <button onClick={() => setEditing('new')} data-testid="custom-sop-new-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
            style={{ backgroundColor: M.color }}>
            <FilePlus2 className="w-4 h-4" /> New custom SOP
          </button>
        </div>
      </div>

      {err && <p className="text-xs text-red-500 mb-3" data-testid="custom-sop-error">{err}</p>}

      {loading ? (
        <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
      ) : sops.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-border rounded-xl" data-testid="custom-sop-empty">
          <p className="text-sm text-muted-foreground">No custom SOPs yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Click <strong>New custom SOP</strong> or <strong>Import markdown</strong> to create your first one.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="custom-sop-list">
          {sops.map(s => {
            const stepCount = parseMarkdownSteps(s.content).length;
            return (
              <li key={s.sop_id} data-testid={`custom-sop-row-${s.sop_id}`}
                className="grid grid-cols-[1fr_auto] gap-3 items-center px-4 py-3 border border-border rounded-xl hover:bg-accent/30 transition-colors">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{s.title}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {s.category} · v{s.version} · {stepCount} step{stepCount === 1 ? '' : 's'}
                    {s.created_by ? ` · by ${s.created_by}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => onDuplicate(s)} data-testid={`custom-sop-duplicate-${s.sop_id}`}
                    title="Duplicate" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent">
                    <CopyIcon className="w-4 h-4" />
                  </button>
                  <button onClick={() => setEditing(s)} data-testid={`custom-sop-edit-${s.sop_id}`}
                    title="Edit" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => onDelete(s)} data-testid={`custom-sop-delete-${s.sop_id}`}
                    title="Delete" className="p-2 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-accent">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <CustomSOPEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}

      {importing && (
        <BulkImportModal
          onClose={() => setImporting(false)}
          onDone={() => { setImporting(false); load(); }}
        />
      )}
    </div>
  );
}

function CustomSOPEditor({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const [title, setTitle] = useState(initial?.title || '');
  const [category, setCategory] = useState(initial?.category || SOP_CATEGORY_PRESETS[0]);
  const [customCategoryMode, setCustomCategoryMode] = useState(
    initial ? !SOP_CATEGORY_PRESETS.includes(initial.category) : false
  );
  const [customCategory, setCustomCategory] = useState(
    initial && !SOP_CATEGORY_PRESETS.includes(initial.category) ? initial.category : ''
  );
  const [version, setVersion] = useState(initial?.version || '1.0');
  const [isoRef, setIsoRef] = useState(initial?.iso_reference || '');
  const [content, setContent] = useState(initial?.content || '## Purpose\n\nDescribe the SOP\'s purpose here.\n\n## Scope\n\nWho this SOP applies to.\n\n## Procedure\n\nStep-by-step instructions.\n');
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [err, setErr] = useState('');

  const steps = parseMarkdownSteps(content);
  const finalCategory = (customCategoryMode ? customCategory : category).trim();

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!title.trim() || !finalCategory || !content.trim()) {
      setErr('Title, category, and content are required.');
      return;
    }
    setSaving(true);
    const payload = {
      title: title.trim(),
      category: finalCategory,
      version: version.trim() || '1.0',
      iso_reference: isoRef.trim(),
      content,
      video_url: '',
    };
    try {
      if (isNew) {
        await axios.post(`${API}/sops`, payload, { withCredentials: true });
      } else {
        await axios.put(`${API}/sops/${initial.sop_id}`, payload, { withCredentials: true });
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <Modal title={isNew ? 'Create custom SOP' : 'Edit custom SOP'} onClose={onClose} size="lg">
      <form onSubmit={submit} className="space-y-4" data-testid="custom-sop-editor">
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3">
          <Input label="Title" value={title} onChange={setTitle} required testid="custom-sop-title" />
          <Input label="Version" value={version} onChange={setVersion} testid="custom-sop-version" />
        </div>

        <div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Category</span>
          <div className="flex items-center gap-2 mt-1">
            {customCategoryMode ? (
              <input type="text" value={customCategory} onChange={e => setCustomCategory(e.target.value)}
                placeholder="New category name"
                data-testid="custom-sop-category-custom"
                className="flex-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
            ) : (
              <select value={category} onChange={e => setCategory(e.target.value)}
                data-testid="custom-sop-category-select"
                className="flex-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
                {SOP_CATEGORY_PRESETS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <button type="button"
              onClick={() => { setCustomCategoryMode(m => !m); setCustomCategory(''); }}
              data-testid="custom-sop-category-toggle"
              className="text-[10px] font-semibold px-2 py-2 rounded-lg border border-border hover:bg-accent">
              {customCategoryMode ? 'Pick preset' : '+ Add new'}
            </button>
          </div>
        </div>

        <Input label="ISO / regulatory reference (optional)" value={isoRef} onChange={setIsoRef} testid="custom-sop-iso" />

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Markdown content · {steps.length} step{steps.length === 1 ? '' : 's'} detected
            </span>
            <button type="button" onClick={() => setShowPreview(p => !p)}
              data-testid="custom-sop-preview-toggle"
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md border border-border hover:bg-accent">
              <Eye className="w-3 h-3" /> {showPreview ? 'Hide' : 'Show'} step preview
            </button>
          </div>
          <textarea value={content} onChange={e => setContent(e.target.value)}
            required rows={14}
            data-testid="custom-sop-content"
            className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 font-mono leading-relaxed" />
          <p className="text-[11px] text-muted-foreground mt-1">
            Tip: start each step with <code className="font-mono bg-muted px-1 rounded">##&nbsp;Step&nbsp;title</code>.
            The walkthrough splits on every <code className="font-mono bg-muted px-1 rounded">##</code> heading.
          </p>
        </div>

        {showPreview && (
          <div className="border border-border rounded-lg p-3 bg-muted/20" data-testid="custom-sop-preview">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Walkthrough preview</p>
            {steps.length === 0 ? (
              <p className="text-xs text-muted-foreground">No steps detected — add <code className="font-mono">##</code> headings.</p>
            ) : (
              <ol className="space-y-2">
                {steps.map((s, i) => (
                  <li key={i} data-testid={`custom-sop-preview-step-${i}`}
                    className="pl-3 border-l-2" style={{ borderColor: M.color }}>
                    <p className="text-sm font-semibold text-foreground">{i + 1}. {s.title}</p>
                    <p className="text-[11px] text-muted-foreground whitespace-pre-wrap line-clamp-3">{s.body || '(no body)'}</p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        {err && <p className="text-xs text-red-500" data-testid="custom-sop-editor-error">{err}</p>}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button type="button" onClick={onClose}
            className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
          <button type="submit" disabled={saving}
            data-testid="custom-sop-save"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: M.color }}>
            <CheckCircle2 className="w-3.5 h-3.5" /> {saving ? 'Saving…' : (isNew ? 'Create SOP' : 'Save changes')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bulk Markdown Import
// Admin picks 1+ .md files, reviews auto-detected titles, picks a default
// category, then batches POST /api/sops one-by-one and shows a result summary.
// ─────────────────────────────────────────────────────────────────────────────
function BulkImportModal({ onClose, onDone }) {
  const [files, setFiles] = useState([]); // [{ name, title, content, include, status, error }]
  const [category, setCategory] = useState(SOP_CATEGORY_PRESETS[0]);
  const [customCategoryMode, setCustomCategoryMode] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [version, setVersion] = useState('1.0');
  const [importing, setImporting] = useState(false);
  const [finished, setFinished] = useState(false);

  const finalCategory = (customCategoryMode ? customCategory : category).trim();
  const includedCount = files.filter(f => f.include).length;

  // Extract title: first H1 line (# Title) OR filename sans extension, title-cased.
  const detectTitle = (filename, content) => {
    const h1 = content.split('\n').find(ln => /^#\s+.+/.test(ln));
    if (h1) return h1.replace(/^#\s+/, '').trim();
    const base = filename.replace(/\.(md|markdown|txt)$/i, '').replace(/[-_]+/g, ' ');
    return base.replace(/\b\w/g, c => c.toUpperCase()).trim();
  };

  const onPick = async (fileList) => {
    const arr = Array.from(fileList);
    const parsed = await Promise.all(arr.map(async f => {
      const text = await f.text();
      return {
        name: f.name,
        title: detectTitle(f.name, text),
        content: text,
        include: true,
        status: 'pending',
        error: '',
      };
    }));
    setFiles(prev => [...prev, ...parsed]);
  };

  const updateFile = (idx, patch) => setFiles(fs => fs.map((f, i) => i === idx ? { ...f, ...patch } : f));
  const removeFile = (idx) => setFiles(fs => fs.filter((_, i) => i !== idx));

  const runImport = async () => {
    if (!finalCategory) { alert('Pick a category first.'); return; }
    setImporting(true);
    const next = [...files];
    for (let i = 0; i < next.length; i++) {
      if (!next[i].include) continue;
      if (!next[i].title.trim() || !next[i].content.trim()) {
        next[i] = { ...next[i], status: 'failed', error: 'Empty title or content' };
        setFiles([...next]);
        continue;
      }
      try {
        await axios.post(`${API}/sops`, {
          title: next[i].title.trim(),
          category: finalCategory,
          version: version.trim() || '1.0',
          iso_reference: '',
          content: next[i].content,
          video_url: '',
        }, { withCredentials: true });
        next[i] = { ...next[i], status: 'success' };
      } catch (e) {
        next[i] = { ...next[i], status: 'failed', error: e?.response?.data?.detail || 'Failed' };
      }
      setFiles([...next]);
    }
    setImporting(false);
    setFinished(true);
  };

  const successCount = files.filter(f => f.status === 'success').length;
  const failedCount = files.filter(f => f.status === 'failed').length;

  return (
    <Modal title="Bulk-import SOPs from markdown" onClose={finished ? onDone : onClose} size="lg">
      <div className="space-y-4" data-testid="bulk-import-modal">
        {!finished ? (
          <>
            <p className="text-xs text-muted-foreground">
              Drop in one or more <code className="font-mono bg-muted px-1 rounded">.md</code> files. We&apos;ll auto-detect a title from the first <code className="font-mono bg-muted px-1 rounded"># Heading</code> (or the filename) — you can rename each before importing. Steps split on every <code className="font-mono bg-muted px-1 rounded">##</code> heading.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Default category (applies to all)</span>
                <div className="flex items-center gap-2 mt-1">
                  {customCategoryMode ? (
                    <input type="text" value={customCategory} onChange={e => setCustomCategory(e.target.value)}
                      placeholder="New category name"
                      data-testid="bulk-category-custom"
                      className="flex-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
                  ) : (
                    <select value={category} onChange={e => setCategory(e.target.value)}
                      data-testid="bulk-category-select"
                      className="flex-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
                      {SOP_CATEGORY_PRESETS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                  <button type="button"
                    onClick={() => { setCustomCategoryMode(m => !m); setCustomCategory(''); }}
                    data-testid="bulk-category-toggle"
                    className="text-[10px] font-semibold px-2 py-2 rounded-lg border border-border hover:bg-accent">
                    {customCategoryMode ? 'Pick preset' : '+ Add new'}
                  </button>
                </div>
              </div>
              <Input label="Default version" value={version} onChange={setVersion} testid="bulk-version" />
            </div>

            <label className="block border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary/60 cursor-pointer transition-colors" data-testid="bulk-file-dropzone">
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <span className="block text-sm font-semibold text-foreground">Click to pick markdown files</span>
              <span className="block text-[11px] text-muted-foreground mt-1">.md · .markdown · .txt · Multi-select supported</span>
              <input type="file" accept=".md,.markdown,.txt,text/markdown,text/plain"
                multiple
                data-testid="bulk-file-input"
                className="hidden"
                onChange={e => { onPick(e.target.files); e.target.value = ''; }} />
            </label>

            {files.length > 0 && (
              <div className="border border-border rounded-xl overflow-hidden" data-testid="bulk-file-list">
                <div className="px-3 py-2 bg-muted/40 border-b border-border flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {files.length} file{files.length === 1 ? '' : 's'} queued · {includedCount} included
                  </span>
                </div>
                <ul className="max-h-72 overflow-y-auto">
                  {files.map((f, idx) => (
                    <li key={idx} data-testid={`bulk-file-row-${idx}`}
                      className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-3 py-2 border-b border-border/40 last:border-0">
                      <input type="checkbox" checked={f.include} disabled={importing}
                        onChange={() => updateFile(idx, { include: !f.include })}
                        data-testid={`bulk-file-include-${idx}`}
                        className="w-4 h-4 rounded border-border" />
                      <div className="min-w-0">
                        <input type="text" value={f.title} disabled={importing}
                          onChange={e => updateFile(idx, { title: e.target.value })}
                          data-testid={`bulk-file-title-${idx}`}
                          className="w-full text-sm bg-transparent border-0 border-b border-transparent focus:border-border focus:outline-none text-foreground font-medium" />
                        <p className="text-[11px] text-muted-foreground truncate">
                          {f.name} · {parseMarkdownSteps(f.content).length} steps · {(f.content.length / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button type="button" onClick={() => removeFile(idx)} disabled={importing}
                        data-testid={`bulk-file-remove-${idx}`}
                        className="p-1.5 text-muted-foreground hover:text-red-500">
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={onClose} disabled={importing}
                className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
              <button type="button" onClick={runImport}
                disabled={importing || includedCount === 0}
                data-testid="bulk-import-submit"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white disabled:opacity-50"
                style={{ backgroundColor: M.color }}>
                <Upload className="w-3.5 h-3.5" /> {importing ? `Importing… (${files.filter(f => f.status === 'success').length}/${includedCount})` : `Import ${includedCount} SOP${includedCount === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3" data-testid="bulk-import-result">
              <div className="p-4 rounded-xl border border-green-500/30 bg-green-500/5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-green-600">Imported</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{successCount}</p>
              </div>
              <div className="p-4 rounded-xl border border-red-500/30 bg-red-500/5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-red-600">Failed</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{failedCount}</p>
              </div>
            </div>
            {failedCount > 0 && (
              <ul className="border border-border rounded-xl max-h-48 overflow-y-auto" data-testid="bulk-import-failed-list">
                {files.filter(f => f.status === 'failed').map((f, i) => (
                  <li key={i} className="px-3 py-2 border-b border-border/40 last:border-0">
                    <p className="text-sm font-semibold text-foreground">{f.title}</p>
                    <p className="text-[11px] text-red-500">{f.error}</p>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={onDone}
                data-testid="bulk-import-done"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white"
                style={{ backgroundColor: M.color }}>
                <CheckCircle2 className="w-3.5 h-3.5" /> Done
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Custom Prep Guide Manager
// Admin / Superuser only. Lists custom (is_custom:true) prep guides; supports
// create, edit, delete, duplicate. Seeded first-party prep notes are hidden
// from this UI and cannot be mutated (enforced server-side by 403 gate).
// ─────────────────────────────────────────────────────────────────────────────

const PREP_MODALITIES = ['CT', 'Ultrasound', 'X-ray', 'DEXA', 'Dental', 'General', 'Special Patient Groups'];
const PREP_SUBCATEGORIES = ['Diagnostic scan', 'Image-guided procedure', 'Vascular', 'Special group', 'Overview', 'Other'];
const PREP_TAG_OPTIONS = [
  'CT-CONTRAST-RISK', 'METFORMIN', 'RENAL-FUNCTION-REQUIRED', 'THYROID-CHECK',
  'BLOOD-THINNER', 'DIABETIC-STEROID-WARNING', 'DRIVER-REQUIRED', 'FASTING',
  'FULL-BLADDER', 'PREGNANCY-CHECK', 'PROCEDURE-CONSENT',
];

function CustomPrepManager() {
  const [guides, setGuides] = useState([]);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/prep-guides`, { withCredentials: true });
      setGuides(r.data.filter(g => g.is_custom));
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load custom prep guides');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onSaved = () => { setEditing(null); load(); };

  const onDelete = async (g) => {
    if (!window.confirm(`Delete "${g.title}"?`)) return;
    try {
      await axios.delete(`${API}/prep-guides/${g.guide_id}`, { withCredentials: true });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const onDuplicate = async (g) => {
    try {
      await axios.post(`${API}/prep-guides`, {
        title: `${g.title} (copy)`,
        modality: g.modality,
        subcategory: g.subcategory || '',
        content: g.content,
        alerts: g.alerts || [],
        sort_order: null,
      }, { withCredentials: true });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Duplicate failed');
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-10" data-testid="mgmt-custom-prep">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" style={{ color: M.color }} />
            <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Custom prep guide library</h2>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Patient-facing prep notes authored by your team. Appear in <code className="font-mono bg-muted px-1 rounded">/preparation</code> alongside seeded SP guides.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setImporting(true)} data-testid="custom-prep-import-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border"
            style={{ borderColor: `${M.color}40`, color: M.color }}>
            <Upload className="w-4 h-4" /> Import markdown
          </button>
          <button onClick={() => setEditing('new')} data-testid="custom-prep-new-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
            style={{ backgroundColor: M.color }}>
            <FilePlus2 className="w-4 h-4" /> New prep guide
          </button>
        </div>
      </div>

      {err && <p className="text-xs text-red-500 mb-3" data-testid="custom-prep-error">{err}</p>}

      {loading ? (
        <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
      ) : guides.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-border rounded-xl" data-testid="custom-prep-empty">
          <p className="text-sm text-muted-foreground">No custom prep guides yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Click <strong>New prep guide</strong> to add one — it becomes available to every staff member in <code className="font-mono">/preparation</code>.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="custom-prep-list">
          {guides.map(g => (
            <li key={g.guide_id} data-testid={`custom-prep-row-${g.guide_id}`}
              className="grid grid-cols-[1fr_auto] gap-3 items-center px-4 py-3 border border-border rounded-xl hover:bg-accent/30 transition-colors">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{g.title}</p>
                <p className="text-[11px] text-muted-foreground">
                  {g.modality}{g.subcategory ? ` · ${g.subcategory}` : ''}
                  {(g.alerts || []).length > 0 ? ` · ${g.alerts.length} alert${g.alerts.length === 1 ? '' : 's'}` : ''}
                  {g.created_by ? ` · by ${g.created_by}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => onDuplicate(g)} data-testid={`custom-prep-duplicate-${g.guide_id}`}
                  title="Duplicate" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent">
                  <CopyIcon className="w-4 h-4" />
                </button>
                <button onClick={() => setEditing(g)} data-testid={`custom-prep-edit-${g.guide_id}`}
                  title="Edit" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => onDelete(g)} data-testid={`custom-prep-delete-${g.guide_id}`}
                  title="Delete" className="p-2 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-accent">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <CustomPrepEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}

      {importing && (
        <BulkPrepImportModal
          onClose={() => setImporting(false)}
          onDone={() => { setImporting(false); load(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bulk Markdown Prep Guide Import
// Admin picks 1+ .md files, each becomes a new custom prep guide. Title is
// detected from first `# Heading` or filename; default modality/subcategory/
// alerts apply to all files in the batch.
// ─────────────────────────────────────────────────────────────────────────────
function BulkPrepImportModal({ onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [modality, setModality] = useState(PREP_MODALITIES[0]);
  const [subcategory, setSubcategory] = useState(PREP_SUBCATEGORIES[0]);
  const [alerts, setAlerts] = useState([]);
  const [importing, setImporting] = useState(false);
  const [finished, setFinished] = useState(false);

  const includedCount = files.filter(f => f.include).length;

  const detectTitle = (filename, content) => {
    const h1 = content.split('\n').find(ln => /^#\s+.+/.test(ln));
    if (h1) return h1.replace(/^#\s+/, '').trim();
    const base = filename.replace(/\.(md|markdown|txt)$/i, '').replace(/[-_]+/g, ' ');
    return base.replace(/\b\w/g, c => c.toUpperCase()).trim();
  };

  const onPick = async (fileList) => {
    const arr = Array.from(fileList);
    const parsed = await Promise.all(arr.map(async f => {
      const text = await f.text();
      return {
        name: f.name,
        title: detectTitle(f.name, text),
        content: text,
        include: true,
        status: 'pending',
        error: '',
      };
    }));
    setFiles(prev => [...prev, ...parsed]);
  };

  const updateFile = (idx, patch) => setFiles(fs => fs.map((f, i) => i === idx ? { ...f, ...patch } : f));
  const removeFile = (idx) => setFiles(fs => fs.filter((_, i) => i !== idx));
  const toggleAlert = (t) => setAlerts(a => a.includes(t) ? a.filter(x => x !== t) : [...a, t]);

  const runImport = async () => {
    setImporting(true);
    const next = [...files];
    for (let i = 0; i < next.length; i++) {
      if (!next[i].include) continue;
      if (!next[i].title.trim() || !next[i].content.trim()) {
        next[i] = { ...next[i], status: 'failed', error: 'Empty title or content' };
        setFiles([...next]);
        continue;
      }
      try {
        await axios.post(`${API}/prep-guides`, {
          title: next[i].title.trim(),
          modality,
          subcategory: subcategory === 'Other' ? '' : subcategory,
          content: next[i].content,
          alerts,
          sort_order: null,
        }, { withCredentials: true });
        next[i] = { ...next[i], status: 'success' };
      } catch (e) {
        next[i] = { ...next[i], status: 'failed', error: e?.response?.data?.detail || 'Failed' };
      }
      setFiles([...next]);
    }
    setImporting(false);
    setFinished(true);
  };

  const successCount = files.filter(f => f.status === 'success').length;
  const failedCount = files.filter(f => f.status === 'failed').length;

  return (
    <Modal title="Bulk-import prep guides from markdown" onClose={finished ? onDone : onClose} size="lg">
      <div className="space-y-4" data-testid="bulk-prep-import-modal">
        {!finished ? (
          <>
            <p className="text-xs text-muted-foreground">
              Drop in one or more <code className="font-mono bg-muted px-1 rounded">.md</code> files — each becomes a new custom prep guide. Title is auto-detected from the first <code className="font-mono bg-muted px-1 rounded"># Heading</code> (or filename). Use <code className="font-mono bg-muted px-1 rounded">## Section</code> for Preparation / Please tell us / Afterwards blocks.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Default modality</span>
                <select value={modality} onChange={e => setModality(e.target.value)}
                  data-testid="bulk-prep-modality"
                  className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
                  {PREP_MODALITIES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Default subcategory</span>
                <select value={subcategory} onChange={e => setSubcategory(e.target.value)}
                  data-testid="bulk-prep-subcategory"
                  className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
                  {PREP_SUBCATEGORIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>

            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Default booking alerts (apply to all)</span>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="bulk-prep-alerts">
                {PREP_TAG_OPTIONS.map(t => {
                  const active = alerts.includes(t);
                  return (
                    <button key={t} type="button" onClick={() => toggleAlert(t)}
                      data-testid={`bulk-prep-alert-${t}`}
                      className="text-[10px] font-semibold px-2 py-1 rounded-full border transition-colors"
                      style={active
                        ? { backgroundColor: `${M.color}15`, borderColor: `${M.color}40`, color: M.color }
                        : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="block border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary/60 cursor-pointer transition-colors" data-testid="bulk-prep-dropzone">
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <span className="block text-sm font-semibold text-foreground">Click to pick markdown files</span>
              <span className="block text-[11px] text-muted-foreground mt-1">.md · .markdown · .txt · Multi-select supported</span>
              <input type="file" accept=".md,.markdown,.txt,text/markdown,text/plain"
                multiple
                data-testid="bulk-prep-file-input"
                className="hidden"
                onChange={e => { onPick(e.target.files); e.target.value = ''; }} />
            </label>

            {files.length > 0 && (
              <div className="border border-border rounded-xl overflow-hidden" data-testid="bulk-prep-file-list">
                <div className="px-3 py-2 bg-muted/40 border-b border-border flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {files.length} file{files.length === 1 ? '' : 's'} queued · {includedCount} included
                  </span>
                </div>
                <ul className="max-h-72 overflow-y-auto">
                  {files.map((f, idx) => (
                    <li key={idx} data-testid={`bulk-prep-row-${idx}`}
                      className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-3 py-2 border-b border-border/40 last:border-0">
                      <input type="checkbox" checked={f.include} disabled={importing}
                        onChange={() => updateFile(idx, { include: !f.include })}
                        data-testid={`bulk-prep-include-${idx}`}
                        className="w-4 h-4 rounded border-border" />
                      <div className="min-w-0">
                        <input type="text" value={f.title} disabled={importing}
                          onChange={e => updateFile(idx, { title: e.target.value })}
                          data-testid={`bulk-prep-title-${idx}`}
                          className="w-full text-sm bg-transparent border-0 border-b border-transparent focus:border-border focus:outline-none text-foreground font-medium" />
                        <p className="text-[11px] text-muted-foreground truncate">
                          {f.name} · {(f.content.length / 1024).toFixed(1)} KB
                          {f.status === 'success' && <span className="ml-2 text-green-600 font-semibold">Imported</span>}
                          {f.status === 'failed' && <span className="ml-2 text-red-500 font-semibold">{f.error}</span>}
                        </p>
                      </div>
                      <button type="button" onClick={() => removeFile(idx)} disabled={importing}
                        data-testid={`bulk-prep-remove-${idx}`}
                        className="p-1.5 text-muted-foreground hover:text-red-500">
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={onClose} disabled={importing}
                className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
              <button type="button" onClick={runImport}
                disabled={importing || includedCount === 0}
                data-testid="bulk-prep-submit"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white disabled:opacity-50"
                style={{ backgroundColor: M.color }}>
                <Upload className="w-3.5 h-3.5" /> {importing ? `Importing… (${files.filter(f => f.status === 'success').length}/${includedCount})` : `Import ${includedCount} guide${includedCount === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3" data-testid="bulk-prep-result">
              <div className="p-4 rounded-xl border border-green-500/30 bg-green-500/5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-green-600">Imported</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{successCount}</p>
              </div>
              <div className="p-4 rounded-xl border border-red-500/30 bg-red-500/5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-red-600">Failed</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{failedCount}</p>
              </div>
            </div>
            {failedCount > 0 && (
              <ul className="border border-border rounded-xl max-h-48 overflow-y-auto" data-testid="bulk-prep-failed-list">
                {files.filter(f => f.status === 'failed').map((f, i) => (
                  <li key={i} className="px-3 py-2 border-b border-border/40 last:border-0">
                    <p className="text-sm font-semibold text-foreground">{f.title}</p>
                    <p className="text-[11px] text-red-500">{f.error}</p>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={onDone}
                data-testid="bulk-prep-done"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white"
                style={{ backgroundColor: M.color }}>
                <CheckCircle2 className="w-3.5 h-3.5" /> Done
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function CustomPrepEditor({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const [title, setTitle] = useState(initial?.title || '');
  const [modality, setModality] = useState(initial?.modality || PREP_MODALITIES[0]);
  const [subcategory, setSubcategory] = useState(initial?.subcategory || PREP_SUBCATEGORIES[0]);
  const [alerts, setAlerts] = useState(initial?.alerts || []);
  const [content, setContent] = useState(initial?.content || '## Preparation\n\n- Fast for X hours.\n- Bring referral, Medicare card.\n\n## Please tell us\n\n- If you are pregnant or may be pregnant.\n');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const toggleAlert = (t) => setAlerts(a => a.includes(t) ? a.filter(x => x !== t) : [...a, t]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!title.trim() || !content.trim()) { setErr('Title and content are required.'); return; }
    setSaving(true);
    const payload = {
      title: title.trim(),
      modality,
      subcategory: subcategory === 'Other' ? '' : subcategory,
      content,
      alerts,
      sort_order: null,
    };
    try {
      if (isNew) {
        await axios.post(`${API}/prep-guides`, payload, { withCredentials: true });
      } else {
        await axios.put(`${API}/prep-guides/${initial.guide_id}`, payload, { withCredentials: true });
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <Modal title={isNew ? 'Create prep guide' : 'Edit prep guide'} onClose={onClose} size="lg">
      <form onSubmit={submit} className="space-y-4" data-testid="custom-prep-editor">
        <Input label="Exam / procedure title (e.g. 'MRI Knee')" value={title} onChange={setTitle} required testid="custom-prep-title" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Modality</span>
            <select value={modality} onChange={e => setModality(e.target.value)}
              data-testid="custom-prep-modality"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
              {PREP_MODALITIES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Subcategory</span>
            <select value={subcategory} onChange={e => setSubcategory(e.target.value)}
              data-testid="custom-prep-subcategory"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
              {PREP_SUBCATEGORIES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>

        <div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Booking alerts</span>
          <p className="text-[11px] text-muted-foreground mb-2">Tick the flags reception must see for this exam.</p>
          <div className="flex flex-wrap gap-1.5" data-testid="custom-prep-alerts">
            {PREP_TAG_OPTIONS.map(t => {
              const active = alerts.includes(t);
              return (
                <button key={t} type="button"
                  onClick={() => toggleAlert(t)}
                  data-testid={`custom-prep-alert-${t}`}
                  className="text-[10px] font-semibold px-2 py-1 rounded-full border transition-colors"
                  style={active
                    ? { backgroundColor: `${M.color}15`, borderColor: `${M.color}40`, color: M.color }
                    : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Markdown content</span>
          <p className="text-[11px] text-muted-foreground mb-1">
            Use <code className="font-mono bg-muted px-1 rounded">##&nbsp;Heading</code> for sections (e.g. "Preparation", "Please tell us", "Afterwards").
          </p>
          <textarea value={content} onChange={e => setContent(e.target.value)}
            required rows={12}
            data-testid="custom-prep-content"
            className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 font-mono leading-relaxed" />
        </div>

        {err && <p className="text-xs text-red-500" data-testid="custom-prep-editor-error">{err}</p>}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
          <button type="submit" disabled={saving}
            data-testid="custom-prep-save"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: M.color }}>
            <CheckCircle2 className="w-3.5 h-3.5" /> {saving ? 'Saving…' : (isNew ? 'Create guide' : 'Save changes')}
          </button>
        </div>
      </form>
    </Modal>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Screening submissions dashboard
// Admin / Superuser only. Lists every patient-filled screening submission
// with form title, patient name, flag count and status. Click to drill into a
// submission detail modal with every Q/A, consent signature, and status updater.
// ─────────────────────────────────────────────────────────────────────────────
function ScreeningSubmissionsWidget() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formFilter, setFormFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (formFilter) params.append('form_id', formFilter);
      if (statusFilter) params.append('status', statusFilter);
      const r = await axios.get(`${API}/screenings?${params.toString()}`, { withCredentials: true });
      setSubs(r.data);
    } catch { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [formFilter, statusFilter]);

  const formsSet = Array.from(new Set(subs.map(s => s.form_id)));
  const formTitles = Object.fromEntries(subs.map(s => [s.form_id, s.form_title]));

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : '—';
  const statusColor = (st) => ({
    open: '#F59E0B', reviewed: '#2563EB', actioned: '#16A34A',
  }[st] || '#64748B');

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-10" data-testid="mgmt-screenings">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <FileSignature className="w-4 h-4" style={{ color: M.color }} />
            <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Patient screening submissions</h2>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Every tablet-filled screening form is recorded here with flagged items for radiologist review.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={formFilter} onChange={e => setFormFilter(e.target.value)}
            data-testid="screenings-form-filter"
            className="text-xs bg-background border border-border rounded-lg px-2 py-1.5">
            <option value="">All forms</option>
            {formsSet.map(fid => <option key={fid} value={fid}>{formTitles[fid] || fid}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            data-testid="screenings-status-filter"
            className="text-xs bg-background border border-border rounded-lg px-2 py-1.5">
            <option value="">Any status</option>
            <option value="open">Open</option>
            <option value="reviewed">Reviewed</option>
            <option value="actioned">Actioned</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
      ) : subs.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-border rounded-xl" data-testid="screenings-empty">
          <p className="text-sm text-muted-foreground">No submissions yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Hand the tablet to a patient at <code className="font-mono">/screening</code> to start.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="screenings-table">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-3 py-2">Submitted</th>
                <th className="px-3 py-2">Form</th>
                <th className="px-3 py-2">Patient</th>
                <th className="px-3 py-2">Exam</th>
                <th className="px-3 py-2 text-center">Flags</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {subs.map(s => (
                <tr key={s.submission_id} className="border-b border-border/60 last:border-0"
                  data-testid={`screening-row-${s.submission_id}`}>
                  <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmt(s.submitted_at)}</td>
                  <td className="px-3 py-2 text-foreground font-medium">{s.form_title}</td>
                  <td className="px-3 py-2 text-foreground">{s.patient?.name || '—'}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{s.patient?.scheduled_exam || '—'}</td>
                  <td className="px-3 py-2 text-center">
                    {(s.flags || []).length > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: '#F59E0B15', color: '#F59E0B' }}>
                        <AlertTriangle className="w-3 h-3" /> {s.flags.length}
                      </span>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: `${statusColor(s.status)}15`, color: statusColor(s.status) }}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setSelected(s.submission_id)}
                      data-testid={`screening-view-${s.submission_id}`}
                      className="text-[10px] font-semibold px-2 py-1 rounded-md border border-border hover:bg-accent">
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <ScreeningSubmissionDetail
          submissionId={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

function ScreeningSubmissionDetail({ submissionId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/screenings/${submissionId}`, { withCredentials: true })
      .then(r => { setData(r.data); setNote(r.data.submission?.status_note || ''); })
      .catch(() => {});
  }, [submissionId]);

  if (!data) return <Modal title="Loading…" onClose={onClose} size="lg"><p className="text-sm text-muted-foreground">Fetching submission…</p></Modal>;

  const sub = data.submission;
  const form = data.form;
  const qById = Object.fromEntries((form?.questions || []).map(q => [q.id, q]));

  const updateStatus = async (newStatus) => {
    setSaving(true);
    try {
      await axios.put(`${API}/screenings/${submissionId}/status`,
        { status: newStatus, note }, { withCredentials: true });
      onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Update failed');
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Delete this submission permanently?')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/screenings/${submissionId}`, { withCredentials: true });
      onChanged();
    } catch { setSaving(false); }
  };

  return (
    <Modal title={sub.form_title} onClose={onClose} size="lg">
      <div className="space-y-4" data-testid="screening-detail">
        {/* Patient block */}
        <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-xl">
          <div><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Patient</p><p className="text-sm font-semibold text-foreground">{sub.patient?.name || '—'}</p></div>
          <div><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">DOB</p><p className="text-sm text-foreground">{sub.patient?.dob || '—'}</p></div>
          <div><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Phone</p><p className="text-sm text-foreground">{sub.patient?.phone || '—'}</p></div>
          <div><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Exam</p><p className="text-sm text-foreground">{sub.patient?.scheduled_exam || '—'}</p></div>
        </div>

        {/* Flags */}
        {(sub.flags || []).length > 0 && (
          <div className="p-3 border border-amber-500/30 bg-amber-500/5 rounded-xl">
            <p className="text-sm font-semibold text-amber-600 flex items-center gap-1.5 mb-2">
              <AlertTriangle className="w-4 h-4" /> {sub.flags.length} flag{sub.flags.length === 1 ? '' : 's'} for radiologist review
            </p>
            <ul className="text-xs text-foreground space-y-1">
              {sub.flags.map((f, i) => <li key={i}>• {f}</li>)}
            </ul>
          </div>
        )}

        {/* Answers */}
        <div className="space-y-2" data-testid="screening-detail-answers">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Answers</p>
          {(form?.questions || []).map((q, i) => {
            const val = sub.answers?.[q.id];
            const flagged = sub.flags?.includes(q.label);
            return (
              <div key={q.id} className="grid grid-cols-[1fr_auto] gap-3 px-3 py-2 border border-border rounded-lg items-start">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{i + 1}. {q.label}</p>
                </div>
                <span className="text-sm font-semibold shrink-0 max-w-[50%] text-right break-words"
                  style={{ color: flagged ? '#F59E0B' : 'hsl(var(--foreground))' }}>
                  {val === undefined || val === '' ? '—' : String(val)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Consent / signature */}
        {sub.consent_given && (
          <div className="p-3 bg-muted/30 rounded-xl">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Electronic signature</p>
            <p className="text-sm text-foreground mt-1">
              <span className="font-semibold">{sub.patient_typed_name || sub.patient?.name}</span>
              <span className="text-muted-foreground"> · {new Date(sub.submitted_at).toLocaleString()}</span>
            </p>
          </div>
        )}

        {/* Status updater */}
        <div className="pt-3 border-t border-border">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Review note (optional)</p>
          <textarea value={note} onChange={e => setNote(e.target.value)}
            rows={2}
            data-testid="screening-detail-note"
            className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
          <div className="flex items-center justify-between flex-wrap gap-2 mt-3">
            <button onClick={onDelete} disabled={saving}
              data-testid="screening-detail-delete"
              className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-border text-red-500 hover:bg-red-500/10 disabled:opacity-50">
              Delete
            </button>
            <div className="flex items-center gap-2">
              <button onClick={() => updateStatus('open')} disabled={saving}
                data-testid="screening-detail-status-open"
                className="text-xs font-semibold px-3 py-1.5 rounded-lg border" style={{ borderColor: '#F59E0B40', color: '#F59E0B' }}>Open</button>
              <button onClick={() => updateStatus('reviewed')} disabled={saving}
                data-testid="screening-detail-status-reviewed"
                className="text-xs font-semibold px-3 py-1.5 rounded-lg border" style={{ borderColor: '#2563EB40', color: '#2563EB' }}>Reviewed</button>
              <button onClick={() => updateStatus('actioned')} disabled={saving}
                data-testid="screening-detail-status-actioned"
                className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white" style={{ backgroundColor: '#16A34A' }}>Actioned</button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Incident Dashboard (admin / superuser)
// Headline KPIs + filter-and-drill incident table. Deep links into the full
// detail page at /incidents/:id where the RCA + status workflow lives.
// ─────────────────────────────────────────────────────────────────────────────
const INC_CATEGORIES = [
  { value: '',                  label: 'All categories' },
  { value: 'near_miss',         label: 'Near miss' },
  { value: 'patient_safety',    label: 'Patient safety' },
  { value: 'complaint',         label: 'Complaint' },
  { value: 'equipment',         label: 'Equipment' },
  { value: 'staff_injury',      label: 'Staff injury' },
  { value: 'infection_breach',  label: 'Infection breach' },
  { value: 'medication',        label: 'Medication' },
  { value: 'radiation',         label: 'Radiation' },
  { value: 'communication',     label: 'Communication' },
  { value: 'it_system',         label: 'IT / system' },
  { value: 'other',             label: 'Other' },
];
const INC_STATUSES = [
  { value: '',                  label: 'Any status' },
  { value: 'reported',          label: 'Reported', color: '#F59E0B' },
  { value: 'under_review',      label: 'Under review', color: '#2563EB' },
  { value: 'rca_in_progress',   label: 'RCA in progress', color: '#7C3AED' },
  { value: 'closed',            label: 'Closed', color: '#16A34A' },
];
const INC_SEVERITIES = {
  near_miss: { label: 'Near miss', color: '#16A34A' },
  minor:     { label: 'Minor', color: '#F59E0B' },
  moderate:  { label: 'Moderate', color: '#D97706' },
  major:     { label: 'Major', color: '#DC2626' },
};

function IncidentsDashboardWidget() {
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [trendMonths, setTrendMonths] = useState(12);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (catFilter) params.append('category', catFilter);
      if (statusFilter) params.append('status', statusFilter);
      const [s, r, t] = await Promise.all([
        axios.get(`${API}/incidents/stats`, { withCredentials: true }),
        axios.get(`${API}/incidents?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/incidents/trends?months=${trendMonths}`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setRows(r.data);
      setTrends(t.data);
    } catch { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [catFilter, statusFilter, trendMonths]);

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : '—';
  const catName = (v) => INC_CATEGORIES.find(c => c.value === v)?.label || v;
  const statusMeta = (v) => INC_STATUSES.find(s => s.value === v) || { label: v, color: '#64748B' };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-10" data-testid="mgmt-incidents">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" style={{ color: M.color }} />
            <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Incident register</h2>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Every filed report. Open a row to triage, run RCA and close. Near-miss reporting is a leading safety indicator — encourage it.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
            data-testid="incidents-cat-filter"
            className="text-xs bg-background border border-border rounded-lg px-2 py-1.5">
            {INC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            data-testid="incidents-status-filter"
            className="text-xs bg-background border border-border rounded-lg px-2 py-1.5">
            {INC_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* Headline KPIs */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="incidents-stats">
          <Kpi label="Total" value={stats.total} color={M.color} />
          <Kpi label="Open" value={stats.open} color="#F59E0B" />
          <Kpi label="Near misses" value={stats.near_miss} color="#16A34A"
            helper="Leading safety indicator" />
          <Kpi label="Major events" value={stats.major} color={stats.major > 0 ? '#DC2626' : '#64748B'} />
          <Kpi label="Last 30 days" value={stats.last_30_days} color="#2563EB" />
        </div>
      )}

      {/* Trends chart */}
      {trends && <IncidentTrendsChart trends={trends} months={trendMonths} onMonthsChange={setTrendMonths} />}

      {loading ? (
        <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-border rounded-xl" data-testid="incidents-empty">
          <p className="text-sm text-muted-foreground">No incidents match this filter.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="incidents-table">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Severity</th>
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">Submitted by</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(i => {
                const sv = INC_SEVERITIES[i.severity] || { label: i.severity, color: '#64748B' };
                const st = statusMeta(i.status);
                return (
                  <tr key={i.incident_id} className="border-b border-border/60 last:border-0"
                    data-testid={`incident-row-${i.incident_id}`}>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmt(i.submitted_at)}</td>
                    <td className="px-3 py-2 text-foreground font-medium">{catName(i.category)}</td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${sv.color}15`, color: sv.color }}>{sv.label}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{i.location || '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      {i.anonymous ? (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <Lock className="w-3 h-3" /> Anonymous
                        </span>
                      ) : (
                        <span className="text-foreground">{i.submitter?.name || '—'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${st.color}15`, color: st.color }}>{st.label}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link to={`/incidents/${i.incident_id}`}
                        data-testid={`incident-open-${i.incident_id}`}
                        className="text-[10px] font-semibold px-2 py-1 rounded-md border border-border hover:bg-accent">
                        Open
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, color, helper }) {
  return (
    <div className="p-4 rounded-xl border border-border bg-card">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-['Outfit'] text-2xl font-bold mt-1" style={{ color }}>{value}</p>
      {helper && <p className="text-[10px] text-muted-foreground mt-0.5">{helper}</p>}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Consent submissions dashboard (admin)
// Lists every captured consent. Click → /consent/submission/:id where PMs can
// countersign, print / export the PDF, and delete.
// ─────────────────────────────────────────────────────────────────────────────
function ConsentSubmissionsWidget() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.append('status', statusFilter);
    axios.get(`${API}/consents?${params.toString()}`, { withCredentials: true })
      .then(r => setRows(r.data))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : '—';

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-10" data-testid="mgmt-consents">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <FileSignature className="w-4 h-4" style={{ color: M.color }} />
            <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Consent register</h2>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Every signed image-guided procedure consent. Click a row to countersign or print/export the PDF.
          </p>
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          data-testid="consents-status-filter"
          className="text-xs bg-background border border-border rounded-lg px-2 py-1.5">
          <option value="">All consents</option>
          <option value="open">Awaiting countersign</option>
          <option value="countersigned">Countersigned</option>
        </select>
      </div>

      {loading ? (
        <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-border rounded-xl" data-testid="consents-empty">
          <p className="text-sm text-muted-foreground">No consents captured yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Start one at <Link to="/consent" className="underline">/consent</Link>.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="consents-table">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-3 py-2">Signed</th>
                <th className="px-3 py-2">Procedure</th>
                <th className="px-3 py-2">Patient</th>
                <th className="px-3 py-2">Captured by</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const done = r.status === 'countersigned';
                return (
                  <tr key={r.submission_id} className="border-b border-border/60 last:border-0"
                    data-testid={`consent-row-${r.submission_id}`}>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmt(r.submitted_at)}</td>
                    <td className="px-3 py-2 text-foreground font-medium">{r.template_title}</td>
                    <td className="px-3 py-2 text-foreground">{r.patient?.name || '—'}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.submitted_by?.name || '—'}</td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={done
                          ? { backgroundColor: '#16A34A15', color: '#16A34A' }
                          : { backgroundColor: '#F59E0B15', color: '#F59E0B' }}>
                        {done ? 'Countersigned' : 'Awaiting'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link to={`/consent/submission/${r.submission_id}`}
                        data-testid={`consent-open-${r.submission_id}`}
                        className="text-[10px] font-semibold px-2 py-1 rounded-md border border-border hover:bg-accent">
                        Open
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Incident trends chart — monthly counts for the selected window, separates
// total reports (amber) from near-miss (green — leading indicator you want
// to see climb) and major events (red — you want these near zero).
// ─────────────────────────────────────────────────────────────────────────────
function IncidentTrendsChart({ trends, months, onMonthsChange }) {
  const data = (trends.buckets || []).map(b => {
    const [y, m] = b.month.split('-');
    const label = new Date(Number(y), Number(m) - 1, 1)
      .toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
    return { label, total: b.total, near_miss: b.near_miss, major: b.major };
  });
  const hasData = data.some(d => d.total > 0);

  return (
    <div className="bg-card border border-border rounded-2xl p-5 mb-5" data-testid="incidents-trends">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" style={{ color: M.color }} />
            <h3 className="font-['Outfit'] text-sm font-semibold text-foreground">Incident trends</h3>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Monthly reports over the selected window. A healthy reporting culture shows <span className="font-semibold" style={{ color: '#16A34A' }}>near-miss counts rising</span> and <span className="font-semibold" style={{ color: '#DC2626' }}>major events flat or falling</span>.
          </p>
        </div>
        <div className="flex items-center gap-1" data-testid="trends-window">
          {[6, 12, 24].map(m => (
            <button key={m} onClick={() => onMonthsChange(m)}
              data-testid={`trends-window-${m}`}
              className="text-[10px] font-semibold px-2 py-1 rounded-md border transition-colors"
              style={months === m
                ? { backgroundColor: `${M.color}15`, borderColor: `${M.color}40`, color: M.color }
                : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}>
              {m}m
            </button>
          ))}
        </div>
      </div>
      {!hasData ? (
        <div className="h-48 flex items-center justify-center border border-dashed border-border rounded-xl" data-testid="trends-empty">
          <p className="text-xs text-muted-foreground">No incidents in the last {months} month{months === 1 ? '' : 's'}.</p>
        </div>
      ) : (
        <div style={{ width: '100%', height: 220 }}>
          <RC.ResponsiveContainer>
            <RC.BarChart data={data} margin={{ top: 10, right: 10, left: -12, bottom: 0 }}>
              <RC.CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <RC.XAxis dataKey="label" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <RC.YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} width={30} />
              <RC.Tooltip
                cursor={{ fill: 'hsl(var(--muted) / 0.4)' }}
                contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 600 }} />
              <RC.Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
              <RC.Bar dataKey="total" name="All reports" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              <RC.Bar dataKey="near_miss" name="Near miss" fill="#16A34A" radius={[3, 3, 0, 0]} />
              <RC.Bar dataKey="major" name="Major" fill="#DC2626" radius={[3, 3, 0, 0]} />
            </RC.BarChart>
          </RC.ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

